import { useRef, useCallback } from 'react'

// Historical note: this hook used to run a client-side MediaRecorder and keep
// the whole interview as 1s blobs in memory. Video is recorded server-side
// via LiveKit egress and the client blobs were never uploaded, so recording
// here was pure memory growth (~19 MB/min) — enough to crash long interviews
// on mobile. It also meant a device switch mid-interview (which changes the
// stream's track set) killed the recorder. Only the stream acquisition and
// the segment timing bookkeeping (consumed by saveSegmentMetadata) remain.
export function useMediaRecorder() {
  const generationRef = useRef(0)
  const startTimeRef = useRef<number>(0)
  const streamRef = useRef<MediaStream | null>(null)
  const segmentStartRef = useRef<number>(0)

  // Request media permissions early so users can preview camera/mic in settings
  const requestPreview = useCallback(async (): Promise<MediaStream> => {
    if (streamRef.current) return streamRef.current
    const generation = generationRef.current
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { width: { min: 640, ideal: 1280 }, height: { min: 480, ideal: 720 }, frameRate: { ideal: 30 } },
      audio: { sampleRate: 48000, channelCount: 1, echoCancellation: true },
    })
    if (generationRef.current !== generation) {
      stream.getTracks().forEach((track) => track.stop())
      throw new Error('Media request cancelled')
    }
    streamRef.current = stream
    return stream
  }, [])

  const startContinuous = useCallback(async (): Promise<MediaStream> => {
    const generation = generationRef.current
    // Reuse preview stream if already acquired (avoids second permission prompt)
    const stream = streamRef.current ?? await navigator.mediaDevices.getUserMedia({
      video: { width: { min: 640, ideal: 1280 }, height: { min: 480, ideal: 720 }, frameRate: { ideal: 30 } },
      audio: { sampleRate: 48000, channelCount: 1, echoCancellation: true },
    })
    if (generationRef.current !== generation) {
      stream.getTracks().forEach((track) => track.stop())
      throw new Error('Media request cancelled')
    }
    streamRef.current = stream
    startTimeRef.current = Date.now()
    return stream
  }, [])

  const markSegmentStart = useCallback(() => {
    segmentStartRef.current =
      (Date.now() - startTimeRef.current) / 1000
  }, [])

  const markSegmentEnd = useCallback(
    (_segmentName: string): Promise<{ startTime: number; endTime: number }> => {
      const endTime = (Date.now() - startTimeRef.current) / 1000
      return Promise.resolve({ startTime: segmentStartRef.current, endTime })
    },
    []
  )

  const stopStream = useCallback(() => {
    generationRef.current++
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
  }, [])

  return {
    requestPreview,
    startContinuous,
    markSegmentStart,
    markSegmentEnd,
    stopStream,
    streamRef,
  }
}
